import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, MapPin, Droplets, Home, Clock, Leaf, Calendar, Plus, Eye, Edit, Activity, Satellite
} from 'lucide-react'
import { getPaddock, getLantanaPatches, getRemoteSensingTimeseries, getPaddockGrazingEvents, getDroneComposition } from '../../services/api'
import PaddockGrazingTimeline from '../../components/PaddockGrazingTimeline'

export default function PaddockDetail({ ranchId }) {
  const { paddockId } = useParams()
  const navigate = useNavigate()

  // Fetch paddock details
  const { data: paddock, isLoading: paddockLoading, error: paddockError } = useQuery({
    queryKey: ['paddock', paddockId],
    queryFn: () => getPaddock(paddockId),
  })

  // Fetch lantana patches for this paddock
  const { data: allPatches } = useQuery({
    queryKey: ['lantanaPatches', ranchId],
    queryFn: () => getLantanaPatches(ranchId),
  })

  // Fetch remote sensing timeseries
  const { data: vegTimeseries } = useQuery({
    queryKey: ['remoteSensingTimeseries', paddockId],
    queryFn: () => getRemoteSensingTimeseries(paddockId, { limit: 20 }),
    enabled: !!paddockId,
  })

  // This paddock's logged grazing (materialised from the locked calendar).
  const { data: grazingEvents } = useQuery({
    queryKey: ['paddockGrazing', paddockId],
    queryFn: () => getPaddockGrazingEvents(paddockId),
    enabled: !!paddockId,
  })

  // Drone composition: latest flight's structure/cover for this paddock.
  const { data: droneComp } = useQuery({
    queryKey: ['droneComposition', ranchId],
    queryFn: () => getDroneComposition(ranchId),
    enabled: !!ranchId,
    staleTime: 10 * 60_000,
  })

  // Filter patches for this paddock
  const paddockPatches = allPatches?.filter(p => p.paddock_id === paddockId) || []

  const getStatusBadge = (status) => {
    if (status === 'grazing') {
      return { color: 'bg-green-100 text-green-800', label: 'Grazing' }
    }
    if (status === 'ready') {
      return { color: 'bg-blue-100 text-blue-800', label: 'Ready' }
    }
    return { color: 'bg-yellow-100 text-yellow-800', label: 'Resting' }
  }

  const getLantanaStatusBadge = (status) => {
    const badges = {
      identified: { color: 'bg-yellow-100 text-yellow-800', label: 'Identified' },
      scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
      in_progress: { color: 'bg-purple-100 text-purple-800', label: 'In Progress' },
      cleared: { color: 'bg-green-100 text-green-800', label: 'Cleared' },
    }
    return badges[status] || badges.identified
  }

  if (paddockLoading) {
    return <div className="p-6 text-center">Loading paddock details...</div>
  }

  if (paddockError || !paddock) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">Failed to load paddock</p>
        <button
          onClick={() => navigate('/paddocks')}
          className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Back to Paddocks
        </button>
      </div>
    )
  }

  const status = paddock.status || {}
  const statusBadge = getStatusBadge(status.current_status)

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/paddocks')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{paddock.code}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.color}`}>
              {statusBadge.label}
            </span>
          </div>
          <p className="text-gray-600">{paddock.name}</p>
        </div>
        <button
          onClick={() => navigate(`/paddocks/${paddockId}/edit`)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <Edit className="h-4 w-4" />
          Edit
        </button>
      </div>

      {/* Main Info Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Paddock Information</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Area</p>
              <p className="font-medium">{paddock.area_ha ? `${paddock.area_ha} ha` : 'Not set'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Days Rested</p>
              <p className="font-medium">{status.days_rested ?? 0} days</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-sm text-gray-500">Water</p>
              <p className="font-medium">{paddock.has_water ? 'Yes' : 'No'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-amber-400" />
            <div>
              <p className="text-sm text-gray-500">Kraal</p>
              <p className="font-medium">{paddock.has_kraal ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        {/* Additional details */}
        <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-4">
          {paddock.management_type && (
            <div>
              <p className="text-sm text-gray-500">Management Type</p>
              <p className="font-medium capitalize">{paddock.management_type}</p>
            </div>
          )}
          {paddock.terrain_type && (
            <div>
              <p className="text-sm text-gray-500">Terrain</p>
              <p className="font-medium capitalize">{paddock.terrain_type}</p>
            </div>
          )}
          {status.current_grass_cover_pct && (
            <div>
              <p className="text-sm text-gray-500">Grass Cover</p>
              <p className="font-medium">{status.current_grass_cover_pct}%</p>
            </div>
          )}
        </div>
      </div>

      {/* Current Grazing Info (if grazing) */}
      {status.current_status === 'grazing' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-green-900 mb-4">Currently Grazing</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-green-600">Cattle Count</p>
              <p className="text-xl font-bold text-green-900">{status.cattle_count || 0}</p>
            </div>
            {status.current_herd && (
              <div>
                <p className="text-sm text-green-600">Herd</p>
                <p className="text-xl font-bold text-green-900">{status.current_herd}</p>
              </div>
            )}
            {status.grazing_started && (
              <div>
                <p className="text-sm text-green-600">Started</p>
                <p className="text-xl font-bold text-green-900">
                  {new Date(status.grazing_started).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          <p className="mt-4 text-sm text-green-700">
            Manage grazing periods in the <Link to="/grazing" className="underline font-medium">Grazing Calendar</Link>
          </p>
        </div>
      )}

      {/* Lantana Patches Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Lantana Patches</h2>
          </div>
          <Link
            to={`/lantana/new?paddock=${paddockId}`}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
          >
            <Plus className="h-4 w-4" />
            Add Patch
          </Link>
        </div>

        {paddockPatches.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Leaf className="h-10 w-10 mx-auto text-gray-300 mb-2" />
            <p>No lantana patches identified in this paddock</p>
            <Link
              to={`/lantana/new?paddock=${paddockId}`}
              className="inline-flex items-center gap-1 mt-2 text-green-600 hover:text-green-700"
            >
              <Plus className="h-4 w-4" />
              Add a patch
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {paddockPatches.map((patch) => {
              const patchStatus = getLantanaStatusBadge(patch.status)
              
              return (
                <div
                  key={patch.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-gray-900">{patch.patch_code}</p>
                      <p className="text-sm text-gray-600">
                        {patch.area_ha ? `${patch.area_ha} ha` : 'Area TBD'} • {patch.density || 'Unknown'} density
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${patchStatus.color}`}>
                      {patchStatus.label}
                    </span>
                    <Link
                      to={`/lantana/${patch.id}/edit`}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Summary */}
        {paddockPatches.length > 0 && (
          <div className="mt-4 pt-4 border-t flex gap-4 text-sm">
            <span className="text-gray-600">
              <strong>{paddockPatches.length}</strong> patches total
            </span>
            <span className="text-yellow-600">
              <strong>{paddockPatches.filter(p => p.status === 'identified').length}</strong> identified
            </span>
            <span className="text-blue-600">
              <strong>{paddockPatches.filter(p => p.status === 'scheduled').length}</strong> scheduled
            </span>
            <span className="text-green-600">
              <strong>{paddockPatches.filter(p => p.status === 'cleared').length}</strong> cleared
            </span>
          </div>
        )}
      </div>

      {/* Vegetation Trends */}
      <VegetationTrends
        timeseries={vegTimeseries}
        currentNdvi={status.current_ndvi}
        vegetationHealth={status.vegetation_health}
        lastNdviDate={status.last_ndvi_date}
        grassCoverPct={status.current_grass_cover_pct}
        lantanaPct={status.current_lantana_pct}
      />

      {/* Drone survey composition — structure the satellite can't see. */}
      <DroneSurveyCard comp={droneComp} paddockCode={paddock.code} />

      {/* This paddock's grazing history — the satellite time-lapse over the last
          year, then the manager's logged rotation. (The whole-ranch verification
          map now lives on the Paddocks overview.) */}
      <PaddockGrazingTimeline ranchId={ranchId} paddockId={paddockId} />
      <GrazingHistory events={grazingEvents} />
    </div>
  )
}

// =============================================================================
// GRAZING HISTORY (this paddock's logged grazing events)
// =============================================================================

function GrazingHistory({ events }) {
  const rows = events || []
  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">Logged grazing</h2>
      </div>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-center py-4">No grazing logged for this paddock yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-4">Entered</th><th className="py-1 pr-4">Left</th>
              <th className="py-1 pr-4">Herd</th><th className="py-1 pr-4 text-right">Head</th>
              <th className="py-1 text-right">Days</th>
            </tr></thead>
            <tbody>
              {rows.map((e, i) => {
                const entry = e.entry_date ? new Date(e.entry_date) : null
                const exit = e.exit_date ? new Date(e.exit_date) : null
                const days = entry ? Math.round(((exit || new Date()) - entry) / 864e5) : null
                return (
                  <tr key={e.id || i} className="border-b border-gray-100">
                    <td className="py-1 pr-4 whitespace-nowrap">{entry ? entry.toLocaleDateString() : '—'}</td>
                    <td className="py-1 pr-4 whitespace-nowrap">{exit ? exit.toLocaleDateString() : <span className="text-green-700">active</span>}</td>
                    <td className="py-1 pr-4">{e.herd_group || '—'}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{e.cattle_count ?? '—'}</td>
                    <td className="py-1 text-right tabular-nums">{days ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11px] text-gray-400">
        From the locked grazing calendar. Full ranch view in the{' '}
        <Link to="/grazing" className="text-blue-600 hover:underline">Grazing Calendar</Link>.
      </p>
    </div>
  )
}

// =============================================================================
// VEGETATION TRENDS SECTION
// =============================================================================

function VegetationTrends({ timeseries, currentNdvi, vegetationHealth, lastNdviDate, grassCoverPct, lantanaPct }) {
  const healthColors = {
    stressed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Stressed' },
    moderate: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Moderate' },
    healthy: { bg: 'bg-green-100', text: 'text-green-700', label: 'Healthy' },
    vigorous: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Vigorous' },
  }

  const health = healthColors[vegetationHealth] || null

  // Derive NDVI bar chart data from timeseries (reversed so oldest is first)
  const chartData = (timeseries || []).slice().reverse()
  const maxNdvi = Math.max(...chartData.map(d => d.paddock_ndvi || 0), 1)

  const sourceColors = {
    sentinel2: 'bg-blue-500',
    landsat: 'bg-indigo-500',
    planet: 'bg-purple-500',
    maxar: 'bg-pink-500',
    drone: 'bg-emerald-500',
    model: 'bg-gray-500',
  }

  const hasData = currentNdvi || (timeseries && timeseries.length > 0) || grassCoverPct || lantanaPct

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Satellite className="h-5 w-5 text-indigo-600" />
        <h2 className="text-lg font-semibold text-gray-900">Vegetation Monitoring</h2>
      </div>

      {!hasData ? (
        <div className="text-center py-8 text-gray-500">
          <Activity className="h-10 w-10 mx-auto text-gray-300 mb-2" />
          <p>No vegetation data yet</p>
          <p className="text-xs mt-1">Data will appear after the next monitoring update</p>
        </div>
      ) : (
        <>
          {/* Current Snapshot */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {currentNdvi != null && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Vegetation Score</p>
                <p className="text-xl font-bold text-gray-900">{Number(currentNdvi).toFixed(3)}</p>
                {lastNdviDate && (
                  <p className="text-xs text-gray-400 mt-1">{new Date(lastNdviDate).toLocaleDateString()}</p>
                )}
              </div>
            )}
            {health && (
              <div className={`p-3 rounded-lg ${health.bg}`}>
                <p className="text-xs text-gray-500 mb-1">Health</p>
                <p className={`text-xl font-bold ${health.text}`}>{health.label}</p>
              </div>
            )}
            {grassCoverPct != null && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Grass Cover</p>
                <p className="text-xl font-bold text-green-700">{grassCoverPct}%</p>
              </div>
            )}
            {lantanaPct != null && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Lantana Cover</p>
                <p className="text-xl font-bold text-amber-700">{lantanaPct}%</p>
              </div>
            )}
          </div>

          {/* NDVI Bar Chart (simple CSS bars) */}
          {chartData.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Vegetation Trend</h3>
              <div className="flex items-end gap-1 h-32">
                {chartData.map((d, i) => {
                  const ndvi = d.paddock_ndvi || 0
                  const heightPct = maxNdvi > 0 ? (ndvi / maxNdvi) * 100 : 0
                  const barColor = sourceColors[d.source_type] || 'bg-blue-500'

                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div
                        className={`w-full rounded-t ${barColor} transition-all min-h-[2px]`}
                        style={{ height: `${heightPct}%` }}
                      />
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {ndvi.toFixed(3)} &middot; {d.source_type} &middot; {d.date ? new Date(d.date).toLocaleDateString() : ''}
                      </div>
                    </div>
                  )
                })}
              </div>
              {/* Date labels (first and last) */}
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{chartData[0]?.date ? new Date(chartData[0].date).toLocaleDateString() : ''}</span>
                <span>{chartData.length > 1 && chartData[chartData.length - 1]?.date ? new Date(chartData[chartData.length - 1].date).toLocaleDateString() : ''}</span>
              </div>

              {/* Source legend */}
              <div className="flex flex-wrap gap-3 mt-3">
                {[...new Set(chartData.map(d => d.source_type))].map(src => (
                  <div key={src} className="flex items-center gap-1 text-xs text-gray-500">
                    <div className={`w-2.5 h-2.5 rounded-full ${sourceColors[src] || 'bg-gray-400'}`} />
                    {src}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cover Composition (if available from latest observation) */}
          {timeseries?.[0]?.pasture_cover_pct != null && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Land Cover Composition</h3>
              <CoverBar observation={timeseries[0]} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CoverBar({ observation }) {
  const segments = [
    { key: 'pasture_cover_pct', label: 'Pasture', color: 'bg-green-500' },
    { key: 'lantana_cover_pct', label: 'Lantana', color: 'bg-amber-500' },
    { key: 'tree_cover_pct', label: 'Trees', color: 'bg-emerald-700' },
    { key: 'bare_ground_pct', label: 'Bare', color: 'bg-yellow-300' },
    { key: 'other_cover_pct', label: 'Other', color: 'bg-gray-400' },
  ].filter(s => observation[s.key] != null && Number(observation[s.key]) > 0)

  if (segments.length === 0) return null

  return (
    <div>
      <div className="flex rounded-full h-4 overflow-hidden">
        {segments.map(s => (
          <div
            key={s.key}
            className={`${s.color} transition-all`}
            style={{ width: `${observation[s.key]}%` }}
            title={`${s.label}: ${observation[s.key]}%`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-2">
        {segments.map(s => (
          <div key={s.key} className="flex items-center gap-1 text-xs text-gray-600">
            <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
            {s.label} {Number(observation[s.key]).toFixed(1)}%
          </div>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// DRONE SURVEY (latest flight composition + trends; skeleton for pending
// metrics so the card teaches what's coming, not just what exists)
// =============================================================================
function DroneSurveyCard({ comp, paddockCode }) {
  const d = comp?.paddocks?.[paddockCode]
  const date = d?.observation_date || comp?.observation_date

  const Metric = ({ label, value, unit, delta, deltaGoodWhenDown, note }) => {
    const has = value != null
    let trend = null
    if (has && delta != null && Math.abs(delta) >= (unit === 'm' ? 0.05 : 0.5)) {
      const up = delta > 0
      const bad = deltaGoodWhenDown ? up : !up
      trend = (
        <span className={`text-xs font-medium ${bad ? 'text-red-600' : 'text-green-700'}`}>
          {up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}{unit}
        </span>
      )
    }
    return (
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className={`text-xl font-bold ${has ? 'text-gray-900' : 'text-gray-300'}`}>
            {has ? `${unit === 'm' ? value.toFixed(2) : Math.round(value)}${unit}` : '—'}
          </span>
          {trend}
        </div>
        {note && <p className="text-[11px] text-gray-400 mt-0.5">{note}</p>}
      </div>
    )
  }

  return (
    <div className="section-card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Drone Survey</h2>
        <span className="text-sm text-gray-500">
          {date ? `Flight of ${new Date(date).toLocaleDateString()}` : 'No flight yet'}
        </span>
      </div>
      {!comp?.observation_date ? (
        <p className="text-sm text-gray-400">
          No drone survey yet — composition appears here automatically once a
          flight is ingested.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Metric label="Bush (woody)" value={d?.woody_chm_pct} unit="%"
                    delta={d?.deltas?.woody_chm_pct} deltaGoodWhenDown
                    note="canopy ≥0.5 m, LiDAR" />
            <Metric label="Lantana + shrub" value={d?.lantana_shrub_pct} unit="%"
                    delta={d?.deltas?.lantana_shrub_pct} deltaGoodWhenDown
                    note="green 0.5–5 m canopy" />
            <Metric label="Grass" value={d?.grass_pct} unit="%"
                    delta={d?.deltas?.grass_pct} />
            <Metric label="Bare ground" value={d?.bare_pct} unit="%"
                    delta={d?.deltas?.bare_pct} deltaGoodWhenDown />
            <Metric label="Canopy height" value={d?.canopy_height_mean_m} unit="m"
                    delta={d?.deltas?.canopy_height_mean_m} deltaGoodWhenDown />
            <Metric label="Drone NDVI" value={d?.ndvi != null ? d.ndvi * 100 : null} unit=""
                    delta={d?.deltas?.ndvi != null ? d.deltas.ndvi * 100 : null}
                    note="×100" />
          </div>
          {/* Skeleton: metrics the pipeline will light up when their science
              prerequisites land — visible now so the card sets expectations. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
            <Metric label="True lantana" value={null} unit="%"
                    note="awaiting trained classifier (field labels)" />
            <Metric label="Pasture biomass" value={null} unit=" kg/ha"
                    note="awaiting RTK-positioned ground truth" />
          </div>
          <p className="text-[11px] text-gray-400 mt-3">
            Arrows show change vs the previous flight (red = worsening).
            "Lantana + shrub" is the rule-based class — lantana and other
            green bush together — until the trained classifier separates them.
          </p>
        </>
      )}
    </div>
  )
}
